import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'list-site-branch-changes' as const;

/** Smaller than the API default, so one call doesn't fill an agent's context. */
export const LIST_SITE_BRANCH_CHANGES_DEFAULT_LIMIT = 25;

const argumentZod = z.object({
  siteBranchId: z
    .string()
    .min(1)
    .describe('Site-branch id from list-site-branches.'),
  limit: z
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .describe(
      `Page size (1-100, default ${LIST_SITE_BRANCH_CHANGES_DEFAULT_LIMIT}).`
    ),
  cursor: z
    .string()
    .min(1)
    .optional()
    .describe(
      'Opaque pagination cursor (pagination.nextCursor) from a previous response for the same site branch.'
    ),
});

export const listSiteBranchChangesManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'List site branch changes',
  family: 'site-branches',
  llmTool: {
    name: toolName,
    description:
      'Lists the resources a site-scope branch (Releases) touched, most recently changed first, one page at a time. Returns { changes, pagination }; while pagination.hasMore is true, pass pagination.nextCursor as cursor for the next page. A page can hold fewer than limit changes. A resource edited while you page moves to the front and can be missed; list again from the start for a full view. Each change has resourceType (page, component, style, or cmsItem, with cmsCollectionId), resourceId (for pages, the main page id when there is one), action (added, changed, or removed), name, editedInPrimaryLocale, editedSecondaryLocaleIds, and contributors. A change means the resource was written in the branch, not that it still differs from main. Merged branches keep their changes.',
    inputSchema: argumentZod,
  },
  type: 'server',
  featureGate: FEATURE_FLAGS.SITE_BRANCHES,
  requiredScopes: ['branches:read'],
  permission: [{resource: 'Site', action: 'read'}],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => true,
  isReadOnly: () => true,
});

export type ListSiteBranchChangesArgumentType = z.infer<typeof argumentZod>;
